import { useEffect, useState } from "react";
import { Loader2, MapPinned } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function SetRouteDialog({ ids, base, onClose, onDone }) {
  const [route, setRoute] = useState("");
  const [routes, setRoutes] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!ids) return;
    setRoute("");
    api.get("/route-setup/routes").then((r) => setRoutes(r.data.routes)).catch(() => {});
  }, [ids]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/records/bulk-route`, { ids, route: route.trim() });
      toast.success(`${data.updated} record${data.updated === 1 ? "" : "s"} moved to route ${route.trim() || "(none)"}`);
      onDone();
    } catch (err) { toast.error(formatError(err)); }
    setBusy(false);
  };

  return (
    <Dialog open={!!ids} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-card" data-testid="set-route-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Change route</DialogTitle>
          <DialogDescription>Sets the route on {ids?.length} checked record{ids?.length === 1 ? "" : "s"} in this database. Leave empty to clear it.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="set-route-input">Route</Label>
            <Input id="set-route-input" value={route} onChange={(e) => setRoute(e.target.value)} maxLength={100} placeholder="e.g. B083" className="font-mono" data-testid="set-route-input" />
          </div>
          {routes.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="set-route-options">
              {routes.map((r) => (
                <button key={r.value} type="button" onClick={() => setRoute(r.value)} data-testid={`set-route-option-${r.value}`}
                  className={`rounded-full border px-2.5 py-0.5 text-xs font-mono transition-colors ${route === r.value ? "border-primary bg-primary/15 text-primary" : "hover:border-primary"}`}>
                  {r.value} · {r.count}
                </button>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} data-testid="set-route-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="set-route-save">{busy ? <Loader2 size={15} className="animate-spin" /> : <MapPinned size={15} />} Change route</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
